import { ChatbotEnv, liveOpsSchedule } from './chatbot.ruleset';
import { State } from '../State';
import type { SyncActionAPI } from '../utils/types';
import { getJSTDateTime } from '../utils/time';
import { AlternativeOpenWebBuckets } from '../utils/payload';
import { getRandomInt } from '../utils/random';
import { duration } from '../utils/duration';
import { isGuestUser } from '../guest/guest.getters';
import { sendAnalyticsEvents } from '../analytics';
import { isMessageBlacklistedForGuests } from '../guest/guest.chatbot';

export const getChatbotEnv = (): ChatbotEnv =>
  process.env.IS_DEVELOPMENT ? 'dev' : 'prod';

type ReplicantAPI = Pick<
  SyncActionAPI,
  | 'chatbot'
  | 'postMessage'
  | 'scheduledActions'
  | 'date'
  | 'math'
  | 'sendAnalyticsEvents'
>;
export type ChatbotMessageInstance = Parameters<
  ReplicantAPI['chatbot']['sendMessage']
>[1];

export function deliverChatbotMessage(
  state: State,
  api: ReplicantAPI,
  args: {
    receiverId: string;
    message: ChatbotMessageInstance;

    channel: 'oa' | 'push' | null;
  },
) {
  const { receiverId, message, channel } = args;

  // Don't send 'oa' messages to guests.
  if (isGuestUser({ id: args.receiverId }) && channel === 'oa') {
    return false;
  }

  // Don't send the message if blacklisted for guests.
  if (isMessageBlacklistedForGuests(state, message)) {
    return false;
  }

  let chatbotDeliveryChannel: 'ios' | 'android' | 'chatbot' | undefined =
    undefined;
  if (channel === 'oa') {
    chatbotDeliveryChannel = 'chatbot';
  } else if (channel === 'push') {
    if (api.chatbot.appleDeviceTokenIsValid()) {
      chatbotDeliveryChannel = 'ios';
    }
    if (api.chatbot.androidDeviceTokenIsValid()) {
      chatbotDeliveryChannel = 'android';
    }
  }

  if (chatbotDeliveryChannel) {
    api.chatbot.sendMessage(receiverId, message, {
      forceChannel: chatbotDeliveryChannel,
    });

    // Send push notification analytics
    if (
      chatbotDeliveryChannel === 'ios' ||
      chatbotDeliveryChannel === 'android'
    ) {
      sendAnalyticsEvents(api, [
        {
          eventType: 'PushNotificationSent',
          eventProperties: {
            ...message.payload,
            $channel: chatbotDeliveryChannel,
          },
        },
      ]);
    }

    return true;
  }

  return false;
}

export function scheduleLiveopsMessages(state: State, api: ReplicantAPI) {
  const now = api.date.now();
  const jstNow = getJSTDateTime(now);

  // Find the closest next event
  const nextEvent = liveOpsSchedule
    .map((event) => {
      const weekday = event.day;
      const { hour, minute } = event.time;

      // Get a timestamp of the event
      const eventTime = jstNow.set({ hour, minute, weekday });
      const nextTimestamp =
        eventTime.valueOf() > now ? eventTime : eventTime.plus({ week: 1 });
      return {
        day: event.day,
        type: event.type,
        delayInMS: nextTimestamp.valueOf() - now,
      };
    })
    .sort((a, b) => a.delayInMS - b.delayInMS)[0];

  // Schedule the event
  api.scheduledActions.schedule.sendLiveOpsMessage({
    args: {
      day: nextEvent.day,
      type: nextEvent.type,
    },
    notificationId: 'liveOps',
    delayInMS:
      nextEvent.delayInMS +
      getJitterDelayInMs(api.math.random, duration({ hours: 1 })),
  });
}

export function getAlternativeWebIABBucket(): AlternativeOpenWebBuckets {
  // Force all chatbot URLs to point to MiniApp
  return 'enabled_mini_app';
}

/**
 * Adds a random delay to distribute message load and reduce server pressure.
 * This delay should be added to the delayInMs of scheduled messages to prevent too many messages from being scheduled simultaneously.
 * @param random - A function that generates a random number.
 * @returns A random delay in milliseconds.
 */
export function getJitterDelayInMs(
  random: () => number,
  maxDelayMs?: number,
): number {
  return getRandomInt(0, maxDelayMs ?? duration({ minutes: 5 }), random);
}

/**
 * Generates jitter using an ease-in-out sine curve.
 * Helpful when we want to avoid clustering at either edge of the window.
 */
export function getOptimizedJitterDelayInMs(
  random: () => number,
  maxDelayMs?: number,
): number {
  const maxDelay = maxDelayMs ?? duration({ minutes: 5 });
  const randomValue = random();

  // Apply ease-in-out sine easing to smooth distribution around the middle
  const clamped = Math.max(0, Math.min(1, randomValue));
  const eased = (1 - Math.cos(Math.PI * clamped)) / 2;

  return Math.floor(eased * maxDelay);
}
